// Day-focus blueprints used by Plan Builder's "Suggest workout" button and
// surfaced in the AI form's "Weekly structure" picker. Each blueprint emits
// 2-3 main lifts + 4 accessories + 2 finishers (mirrors the AI prompt rules).
//
// `exercise_id` values must exist in /app/backend/exercises_data.py. If any
// id is unknown to the current library, the picker will skip it.

export const FOCUS_OPTIONS = [
  { id: "UpperStrength", label: "Upper Strength", short: "Upper" },
  { id: "LowerStrength", label: "Lower Strength", short: "Lower" },
  { id: "FullBody", label: "Full Body Strength", short: "Full Body" },
  { id: "Push", label: "Push (Chest/Shoulders/Triceps)", short: "Push" },
  { id: "Pull", label: "Pull (Back/Biceps)", short: "Pull" },
  { id: "Aerobic", label: "Aerobic (Steady State)", short: "Aerobic" },
  { id: "Anaerobic", label: "Anaerobic (Intervals)", short: "Anaerobic" },
  { id: "ACFT", label: "ACFT Prep", short: "ACFT" },
  { id: "Mobility", label: "Mobility / Recovery", short: "Mobility" },
  { id: "Rest", label: "Rest Day", short: "Rest" },
];

// Maps the "Strength|Endurance|..." values already stored on each day object
// to the richer focus id above, so the picker can pre-select correctly.
export const LEGACY_FOCUS_MAP = {
  Strength: "FullBody",
  Endurance: "Aerobic",
  Aerobic: "Aerobic",
  Anaerobic: "Anaerobic",
  Mobility: "Mobility",
  ACFT: "ACFT",
  Recovery: "Rest",
};

// Each blueprint: {plyo:[], main:[], accessory:[], finisher:[]}
// `plyo` is prepended to Strength + Endurance focuses (always 2 explosive
// exercises at the very start of those days). Plyo is omitted on ACFT,
// Mobility, and Rest days by design.
// Entries emit {exercise_id, sets, reps, weight?}.
export const FOCUS_BLUEPRINTS = {
  UpperStrength: {
    legacy_focus: "Strength",
    plyo: [
      { exercise_id: "clap-pushup", sets: 3, reps: "5", weight: "BW" },
      { exercise_id: "medball-slam", sets: 3, reps: "5", weight: "10 lb ball" },
    ],
    main: [
      { exercise_id: "bench-press", sets: 5, reps: "5", weight: "" },
      { exercise_id: "overhead-press", sets: 4, reps: "6", weight: "" },
    ],
    accessory: [
      { exercise_id: "barbell-row", sets: 3, reps: "8-10", weight: "" },
      { exercise_id: "pull-up", sets: 3, reps: "AMRAP", weight: "BW" },
      { exercise_id: "db-incline-press", sets: 3, reps: "10", weight: "" },
      { exercise_id: "band-face-pull", sets: 3, reps: "15", weight: "" },
    ],
    finisher: [
      { exercise_id: "doorway-chest-stretch", sets: 2, reps: "30s/side" },
      { exercise_id: "foam-roll-tspine", sets: 1, reps: "10 reps" },
    ],
  },
  LowerStrength: {
    legacy_focus: "Strength",
    plyo: [
      { exercise_id: "box-jump", sets: 3, reps: "5", weight: "BW" },
      { exercise_id: "broad-jump", sets: 3, reps: "5", weight: "BW" },
    ],
    main: [
      { exercise_id: "back-squat", sets: 5, reps: "5", weight: "" },
      { exercise_id: "barbell-rdl", sets: 4, reps: "6", weight: "" },
    ],
    accessory: [
      { exercise_id: "db-bulgarian-split-squat", sets: 3, reps: "10/side", weight: "" },
      { exercise_id: "glute-bridge", sets: 3, reps: "12", weight: "BW" },
      { exercise_id: "plank", sets: 3, reps: "60s", weight: "BW" },
      { exercise_id: "calf-raise", sets: 3, reps: "15", weight: "" },
    ],
    finisher: [
      { exercise_id: "couch-stretch", sets: 2, reps: "60s/side" },
      { exercise_id: "worlds-greatest-stretch", sets: 2, reps: "5/side" },
    ],
  },
  FullBody: {
    legacy_focus: "Strength",
    plyo: [
      { exercise_id: "box-jump", sets: 3, reps: "5", weight: "BW" },
      { exercise_id: "medball-slam", sets: 3, reps: "5", weight: "10 lb ball" },
    ],
    main: [
      { exercise_id: "back-squat", sets: 4, reps: "6", weight: "" },
      { exercise_id: "bench-press", sets: 4, reps: "6", weight: "" },
      { exercise_id: "barbell-deadlift", sets: 3, reps: "5", weight: "" },
    ],
    accessory: [
      { exercise_id: "pull-up", sets: 3, reps: "AMRAP", weight: "BW" },
      { exercise_id: "db-row-one-arm", sets: 3, reps: "10/side", weight: "" },
      { exercise_id: "farmer-carry", sets: 3, reps: "40m", weight: "" },
      { exercise_id: "hanging-knee-raise", sets: 3, reps: "10", weight: "BW" },
    ],
    finisher: [
      { exercise_id: "worlds-greatest-stretch", sets: 2, reps: "5/side" },
      { exercise_id: "couch-stretch", sets: 1, reps: "60s/side" },
    ],
  },
  Push: {
    legacy_focus: "Strength",
    plyo: [
      { exercise_id: "clap-pushup", sets: 3, reps: "5", weight: "BW" },
      { exercise_id: "medball-slam", sets: 3, reps: "5", weight: "10 lb ball" },
    ],
    main: [
      { exercise_id: "bench-press", sets: 5, reps: "5", weight: "" },
      { exercise_id: "overhead-press", sets: 4, reps: "6", weight: "" },
    ],
    accessory: [
      { exercise_id: "db-incline-press", sets: 3, reps: "10", weight: "" },
      { exercise_id: "dumbbell-press", sets: 3, reps: "10", weight: "" },
      { exercise_id: "skull-crusher", sets: 3, reps: "10", weight: "" },
      { exercise_id: "lateral-raise", sets: 3, reps: "12", weight: "" },
    ],
    finisher: [
      { exercise_id: "doorway-chest-stretch", sets: 2, reps: "30s/side" },
      { exercise_id: "childs-pose", sets: 1, reps: "60s" },
    ],
  },
  Pull: {
    legacy_focus: "Strength",
    plyo: [
      { exercise_id: "medball-slam", sets: 3, reps: "5", weight: "10 lb ball" },
      { exercise_id: "broad-jump", sets: 3, reps: "5", weight: "BW" },
    ],
    main: [
      { exercise_id: "barbell-deadlift", sets: 4, reps: "5", weight: "" },
      { exercise_id: "pull-up", sets: 4, reps: "AMRAP", weight: "BW" },
    ],
    accessory: [
      { exercise_id: "barbell-row", sets: 3, reps: "8", weight: "" },
      { exercise_id: "db-row-one-arm", sets: 3, reps: "10/side", weight: "" },
      { exercise_id: "db-curl", sets: 3, reps: "12", weight: "" },
      { exercise_id: "band-face-pull", sets: 3, reps: "15", weight: "" },
    ],
    finisher: [
      { exercise_id: "foam-roll-lats", sets: 1, reps: "60s/side" },
      { exercise_id: "childs-pose", sets: 1, reps: "60s" },
    ],
  },
  Aerobic: {
    legacy_focus: "Endurance",
    plyo: [
      { exercise_id: "jump-rope", sets: 3, reps: "60s", weight: "BW" },
      { exercise_id: "power-skip", sets: 3, reps: "20m" },
    ],
    main: [
      { exercise_id: "tempo-run", sets: 1, reps: "30 min" },
    ],
    accessory: [
      { exercise_id: "easy-run", sets: 1, reps: "10 min cooldown" },
      { exercise_id: "worlds-greatest-stretch", sets: 2, reps: "5/side" },
    ],
    finisher: [
      { exercise_id: "couch-stretch", sets: 2, reps: "60s/side" },
      { exercise_id: "calf-stretch-wall", sets: 2, reps: "30s/side" },
    ],
  },
  Anaerobic: {
    legacy_focus: "Endurance",
    plyo: [
      { exercise_id: "broad-jump", sets: 3, reps: "5", weight: "BW" },
      { exercise_id: "jump-squat", sets: 3, reps: "5", weight: "BW" },
    ],
    main: [
      { exercise_id: "assault-bike-intervals", sets: 8, reps: "30s/90s rest" },
    ],
    accessory: [
      { exercise_id: "shuttle-sprint-100m", sets: 6, reps: "100m" },
      { exercise_id: "battle-rope-waves", sets: 4, reps: "30s" },
    ],
    finisher: [
      { exercise_id: "couch-stretch", sets: 2, reps: "60s/side" },
      { exercise_id: "calf-stretch-wall", sets: 2, reps: "30s/side" },
    ],
  },
  ACFT: {
    legacy_focus: "ACFT",
    main: [
      { exercise_id: "acft-deadlift", sets: 4, reps: "5", weight: "" },
      { exercise_id: "standing-power-throw", sets: 3, reps: "5", weight: "10 lb ball" },
      { exercise_id: "hand-release-pushup", sets: 3, reps: "AMRAP", weight: "BW" },
    ],
    accessory: [
      { exercise_id: "sprint-drag-carry", sets: 3, reps: "50m" },
      { exercise_id: "leg-tuck", sets: 3, reps: "AMRAP", weight: "BW" },
      { exercise_id: "plank", sets: 3, reps: "60s", weight: "BW" },
      { exercise_id: "two-mile-run", sets: 1, reps: "2 mi" },
    ],
    finisher: [
      { exercise_id: "worlds-greatest-stretch", sets: 2, reps: "5/side" },
      { exercise_id: "couch-stretch", sets: 1, reps: "60s/side" },
    ],
  },
  Mobility: {
    legacy_focus: "Mobility",
    main: [
      { exercise_id: "yoga-flow", sets: 1, reps: "10 min" },
    ],
    accessory: [
      { exercise_id: "foam-roll-tspine", sets: 1, reps: "10 reps" },
      { exercise_id: "foam-roll-quads", sets: 1, reps: "60s/side" },
      { exercise_id: "couch-stretch", sets: 2, reps: "60s/side" },
      { exercise_id: "worlds-greatest-stretch", sets: 2, reps: "5/side" },
    ],
    finisher: [
      { exercise_id: "childs-pose", sets: 1, reps: "90s" },
    ],
  },
  Rest: {
    legacy_focus: "Recovery",
    main: [],
    accessory: [
      { exercise_id: "easy-run", sets: 1, reps: "20-30 min" },
      { exercise_id: "foam-roll-quads", sets: 1, reps: "60s/side" },
    ],
    finisher: [],
  },
};

/** Compute the standard RPE (1-10) for an exercise given its library def + reps.
 *  Mirrors the backend `_default_rpe_for` in server.py so suggested workouts
 *  ship with the same intensity targets that AI-generated plans use.
 */
export function defaultRpeFor(exerciseId, exDef, reps) {
  if (!exDef) return null;
  const PLYO = new Set([
    "box-jump", "broad-jump", "depth-jump", "drop-jump", "clap-pushup",
    "plyo-pushup", "single-leg-broad-jump", "split-squat-jump", "trap-bar-jump",
    "medball-slam", "kettlebell-swing", "standing-power-throw", "burpee-broad-jump",
    "jump-squat", "single-leg-box-jump", "ankle-hop", "pogo-hop", "skater-jump",
    "tuck-jump", "lateral-bound", "box-jump-intervals",
  ]);
  if (PLYO.has(exerciseId)) return "9";
  const domain = (exDef.domain || "").toLowerCase();
  const name = (exDef.name || "").toLowerCase();
  const rstr = (reps || "").toString().trim().toUpperCase();
  if (rstr.includes("AMRAP")) return "9";
  if (domain === "mobility" || /stretch/.test(name) || /^foam-roll/.test(exerciseId || "")) return "3";
  if (domain === "recovery") return "4";
  if (domain === "endurance") {
    if (/sprint|interval|tabata|hill|shuttle|burpee pyramid/.test(name)) return "8-9";
    if (/ruck|tempo/.test(name)) return "6-7";
    return "5";
  }
  if (domain === "acft") return "8-9";
  if (domain === "strength") {
    const m = rstr.match(/^\s*(\d+)\s*(?:-\s*(\d+))?\s*$/);
    if (m) {
      const lo = parseInt(m[1], 10);
      const hi = m[2] ? parseInt(m[2], 10) : lo;
      const avg = (lo + hi) / 2;
      if (avg <= 5) return "8";
      if (avg <= 8) return "7-8";
      return "7";
    }
    return "7";
  }
  return "7";
}

/** Injury substitution rules — keyed on substrings found in an assessment's
 *  free-form notes. Each rule maps a risky exercise_id to a safer alternate
 *  that exists in the library. Order matters: first matching rule wins per ex.
 */
const INJURY_RULES = [
  {
    keywords: ["knee", "knees", "patell", "meniscus", "acl", "mcl"],
    label: "knee-friendly swap",
    swap: {
      "box-jump": "medball-slam",
      "broad-jump": "medball-slam",
      "depth-jump": "medball-slam",
      "drop-jump": "medball-slam",
      "back-squat": "goblet-squat",
      "front-squat": "goblet-squat",
      "barbell-rdl": "trap-bar-rdl",
      "barbell-deadlift": "trap-bar-rdl",
      "db-bulgarian-split-squat": "step-up-weighted",
      "split-squat-jump": "step-up-weighted",
      "walking-lunge": "glute-bridge",
      "jump-squat": "medball-slam",
    },
  },
  {
    keywords: ["lower back", "low back", "lumbar", "disc", "hernia", "spine"],
    label: "back-friendly swap",
    swap: {
      "barbell-deadlift": "trap-bar-rdl",
      "back-squat": "goblet-squat",
      "barbell-row": "seal-row",
      "barbell-rdl": "glute-bridge",
      "stiff-leg-deadlift": "glute-bridge",
      "good-morning": "glute-bridge",
    },
  },
  {
    keywords: ["shoulder", "rotator cuff", "labrum", "ac joint", "impingement"],
    label: "shoulder-friendly swap",
    swap: {
      "overhead-press": "landmine-press",
      "push-press": "landmine-press",
      "bench-press": "dumbbell-press",
      "pull-up": "lat-pulldown",
      "dip": "bench-dip",
      "handstand-pushup": "wall-walk",
    },
  },
  {
    keywords: ["wrist", "elbow", "tendinitis", "tennis elbow"],
    label: "joint-friendly swap",
    swap: {
      "barbell-curl": "db-curl",
      "front-rack-hold": "farmer-carry",
      "handstand-pushup": "wall-walk",
      "clap-pushup": "medball-slam",
      "plyo-pushup": "medball-slam",
    },
  },
  {
    keywords: ["foot", "ankle", "achilles", "plantar"],
    label: "low-impact swap",
    swap: {
      "broad-jump": "medball-slam",
      "box-jump": "medball-slam",
      "depth-jump": "medball-slam",
      "drop-jump": "medball-slam",
      "tempo-run": "bike-tempo",
      "easy-run": "easy-row",
      "sprint-intervals": "assault-bike-intervals",
      "hill-sprints": "assault-bike-intervals",
      "shuttle-sprint-100m": "assault-bike-intervals",
    },
  },
];

function _injurySubstitutions(notes) {
  const out = [];
  if (!notes) return out;
  const lower = notes.toLowerCase();
  for (const rule of INJURY_RULES) {
    if (rule.keywords.some((k) => lower.includes(k))) out.push(rule);
  }
  return out;
}


/** Returns an array of exercise entries ready to drop into a day.
 *  Order: plyo (if any) → main → accessory → finisher.
 *
 *  - assessmentNotes: optional free-form string from the user's saved
 *    assessment. If present, common risky exercises are auto-swapped for
 *    safer alternates from the library (knee, back, shoulder, etc.).
 *  - Every returned entry has an `rpe` value pre-filled to match the
 *    backend's default intensity targets.
 */
export function buildExercisesForFocus(focusId, exerciseMap, assessmentNotes = "") {
  const bp = FOCUS_BLUEPRINTS[focusId];
  if (!bp) return [];
  const all = [
    ...(bp.plyo || []),
    ...(bp.main || []),
    ...(bp.accessory || []),
    ...(bp.finisher || []),
  ];
  const rules = _injurySubstitutions(assessmentNotes);
  return all
    .filter((e) => !exerciseMap || exerciseMap[e.exercise_id])
    .map((entry) => {
      let resolved = { ...entry };
      // Apply first matching injury rule that has a swap for this exercise.
      if (rules.length > 0) {
        for (const rule of rules) {
          const target = rule.swap[resolved.exercise_id];
          if (target && (!exerciseMap || exerciseMap[target])) {
            const oldName = exerciseMap?.[resolved.exercise_id]?.name || resolved.exercise_id;
            resolved.exercise_id = target;
            resolved.notes = `${resolved.notes ? resolved.notes + " · " : ""}${rule.label} (was ${oldName})`;
            break;
          }
        }
      }
      // RPE backfill against the resolved (post-swap) exercise.
      if (!resolved.rpe) {
        resolved.rpe = defaultRpeFor(resolved.exercise_id, exerciseMap?.[resolved.exercise_id], resolved.reps);
      }
      return resolved;
    });
}

export function focusToLegacyLabel(focusId) {
  return FOCUS_BLUEPRINTS[focusId]?.legacy_focus || "Strength";
}
